import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { EmissionService } from '../../core/services/emission.service';
import { EmissionWithTitresDTO } from '../../core/models/emission.model';
import { CustomAction, CustomTableHeader } from '../../core/models/custom-table.model';
import { PageParams } from '../../core/models/page.model';
import { DynamicTableComponent } from '../../shared/components/dynamic-table/dynamic-table.component';
import { DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { EmissionDetailsComponent } from './emission-details/emission-details.component';
import { TitreDetailsComponent } from './titre-details/titre-details.component';
import { TitreViewComponent } from './titre-view/titre-view.component';
import { ResultatDetailsComponent } from './resultat-details/resultat-details.component';
import { customDynamicDialog } from '../../core/utils/utils';
import { CustomDialogData } from '../../core/models/custom-dialog.model';
import { take } from 'rxjs';
import { ToastService } from '../../core/services/toast.service';
import { ConfirmationService } from 'primeng/api';
import { UEMOA_COUNTRIES } from '../../core/models/country.model';
import { SelectModule } from 'primeng/select';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FileUploadModule } from 'primeng/fileupload';
import { DateRangeComponent } from '../../shared/date-range/date-range.component';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'app-emission',
    imports: [
        CommonModule,
        DynamicTableComponent,
        SelectModule,
        FormsModule,
        ReactiveFormsModule,
        ConfirmDialogModule,
        FileUploadModule,
        DateRangeComponent,
        CardModule,
        ButtonModule,
        TooltipModule
    ],
    providers: [DialogService, ConfirmationService],
    templateUrl: './emission.component.html',
    styleUrl: './emission.component.scss'
})
export class EmissionComponent implements OnInit, OnDestroy {
  private emissionService = inject(EmissionService);
  private dialogService = inject(DialogService);
  private toastService = inject(ToastService);
  private confirmationService = inject(ConfirmationService);
  private fb = inject(FormBuilder);

  titres: any[] = [];
  columns: CustomTableHeader[] = [];
  pageable: PageParams = {
    page: 0,
    first: 0,
    rows: 10,
    totalRecords: 0,
    rowPerPageOptions: [10, 20, 50]
  };
  ref: DynamicDialogRef | null = null;
  actions: CustomAction[] = [];
  countries = UEMOA_COUNTRIES;
  rangeDates: Date[] = [];
  minDate: Date | null = null;
  searchForm!: FormGroup;
  typeInstrumentOptions = [
    { label: 'OAT', value: 'OAT' },
    { label: 'BAT', value: 'BAT' }
  ];

  ngOnInit() {
    this.initColumns();
    this.initSearchForm();
    this.loadTitres();
    this.actions = this.getActions();
  }

  initSearchForm() {
    this.searchForm = this.fb.group({
      region: [''],
      typeInstrument: ['']
    });
  }

  ngOnDestroy() {
    if (this.ref) {
      this.ref.close();
    }
  }

  initColumns() {
    this.columns = [
      { column: 'Code ISIN', key: 'isin', type: 'text' },
      { column: 'Région', key: 'region', type: 'country' },
      { column: 'Instrument', key: 'typeInstrument', type: 'text' },
      { column: 'Date d\'opération', key: 'operationDate', type: 'date' },
      { column: 'Date de valeur', key: 'valueDate', type: 'date' },
      { column: 'Échéance', key: 'dueDate', type: 'date' },
      { column: 'Durée (mois)', key: 'duration', type: 'text' },
      { column: 'Taux (%)', key: 'rate', type: 'text' }
    ];
  }

  loadTitres() {
    const formValue = this.searchForm.value;
    const region = formValue.region || undefined;
    const typeInstrument = formValue.typeInstrument || undefined;
    const startDate = this.rangeDates.length === 2 ? this.rangeDates[0] : undefined;
    const endDate = this.rangeDates.length === 2 ? this.rangeDates[1] : undefined;

    this.emissionService.getAllTitres(
      this.pageable.page,
      this.pageable.rows,
      true,
      typeInstrument,
      region,
      startDate,
      endDate
    )
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.titres = response.content.map((titre: any) => {
            // Calculer la durée en mois
            const duration = this.calculateDuration(titre.emission?.operationDate, titre.dueDate);

            return {
              ...titre,
              region: titre.emission?.region || '', // Garder le code pays pour l'affichage du drapeau
              operationDate: titre.emission?.operationDate,
              valueDate: titre.emission?.valueDate,
              amount: titre.emission?.amount,
              duration: duration
            };
          });

          this.pageable = {
            page: response.pageable.pageNumber,
            first: response.pageable.pageNumber * response.pageable.pageSize,
            rows: response.pageable.pageSize,
            totalRecords: response.totalElements,
            rowPerPageOptions: [10, 20, 50]
          };
        },
        error: (error) => {
          this.toastService.showToast('Erreur', 'Erreur lors du chargement des titres', 'error');
          console.error(error);
        }
      });
  }

  calculateDuration(operationDate: string, dueDate: string): number {
    if (!operationDate || !dueDate) return 0;

    const start = new Date(operationDate);
    const end = new Date(dueDate);

    const months = (end.getFullYear() - start.getFullYear()) * 12 +
                   (end.getMonth() - start.getMonth());

    return months;
  }

  onPageChange(event: any) {
    this.pageable.page = event.page;
    this.pageable.rows = event.rows;
    this.pageable.first = event.first;
    this.loadTitres();
  }

  getActions(): CustomAction[] {
    return [
      {
        role: 'READ_EMISSION',
        icon: 'pi pi-info-circle',
        label: 'Consulter',
        severity: 'info',
        emit: (item) => this.viewOrEditTitre(item, false, true),
        visible: true
      },
      {
        role: 'UPDATE_EMISSION',
        icon: 'pi pi-chart-bar',
        label: 'Résultat',
        severity: 'primary',
        emit: (item) => this.handleResultatAction(item),
        visible: true
      },
      {
        role: 'DELETE_EMISSION',
        icon: 'pi pi-trash',
        label: 'Supprimer',
        severity: 'danger',
        emit: (item) => this.deleteTitre(item),
        visible: true
      }
    ];
  }

  createEmission() {
    const data = { isCreate: true } as CustomDialogData;
    this.ref = this.dialogService.open(
      EmissionDetailsComponent,
      customDynamicDialog('Création d\'émission', data, { width: '90vw' })
    );
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadTitres());
  }

  handleResultatAction(titre: any) {
    // Vérifier si un résultat existe déjà pour ce titre
    this.emissionService.getResultatByTitreId(titre.id)
      .pipe(take(1))
      .subscribe({
        next: (resultat) => {
          // Un résultat existe, ouvrir en mode consultation
          this.openResultatDialog(titre, true);
        },
        error: (error) => {
          if (error.status === 404) {
            // Aucun résultat n'existe, ouvrir en mode création
            this.openResultatDialog(titre, false);
          } else {
            this.toastService.showToast('Erreur', 'Erreur lors de la vérification du résultat', 'error');
            console.error(error);
          }
        }
      });
  }

  openResultatDialog(titre: any, isView: boolean) {
    const data = {
      data: titre,
      isView: isView,
      isEdit: !isView
    } as CustomDialogData<any>;

    const title = isView
      ? `Résultat - ${titre.typeInstrument} ${titre.isin ? '(' + titre.isin + ')' : ''}`
      : `Ajouter Résultat - ${titre.typeInstrument}`;

    this.ref = this.dialogService.open(
      ResultatDetailsComponent,
      customDynamicDialog(
        title,
        data,
        { width: '50vw' }
      )
    );
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadTitres());
  }

  viewOrEditTitre(titre: any, isEdit: boolean = false, isView: boolean = false) {
    const data = {
      data: titre,
      isEdit: isEdit,
      isView: isView
    } as CustomDialogData<any>;

    // Utiliser TitreViewComponent pour la consultation
    const component: any = isView ? TitreViewComponent : TitreDetailsComponent;
    const title = isView
      ? `Détails du titre - ${titre.typeInstrument} ${titre.isin ? '(' + titre.isin + ')' : ''}`
      : 'Modification du titre';

    this.ref = this.dialogService.open(
      component,
      customDynamicDialog(
        title,
        data,
        { width: isView ? '60vw' : '50vw' }
      )
    );
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadTitres());
  }

  viewOrEditEmission(emission: EmissionWithTitresDTO, isEdit: boolean = false) {
    const data = {
      data: emission,
      isEdit: isEdit,
      isView: !isEdit
    } as CustomDialogData<EmissionWithTitresDTO>;

    this.ref = this.dialogService.open(
      EmissionDetailsComponent,
      customDynamicDialog(
        isEdit ? 'Modification d\'émission' : 'Détails de l\'émission',
        data,
        { width: '90vw' }
      )
    );
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadTitres());
  }

  deleteTitre(titre: any) {
    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir supprimer ce titre (${titre.typeInstrument} - ${titre.isin || 'Sans ISIN'}) ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Non',
      accept: () => {
        if (titre.id) {
          this.emissionService.deleteEmissionOrTitre(titre.id, true)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showToast('Succès', 'Titre supprimé avec succès', 'success');
                this.loadTitres();
              },
              error: (error) => {
                this.toastService.showToast('Erreur', 'Erreur lors de la suppression du titre', 'error');
                console.error(error);
              }
            });
        }
      }
    });
  }

  deleteEmission(emission: EmissionWithTitresDTO) {
    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir supprimer cette émission et tous ses titres ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Non',
      accept: () => {
        if (emission.id) {
          this.emissionService.deleteEmissionOrTitre(emission.id, false)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showToast('Succès', 'Émission supprimée avec succès', 'success');
                this.loadTitres();
              },
              error: (error) => {
                this.toastService.showToast('Erreur', 'Erreur lors de la suppression de l\'émission', 'error');
                console.error(error);
              }
            });
        }
      }
    });
  }

  onDateRangeSelected(dateRange: Date[]) {
    if (dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      this.rangeDates = dateRange;
      this.minDate = dateRange[0];
    }
  }

  onInvalidRange(message: string) {
    this.toastService.showToast('Erreur', message, 'error');
  }

  searchTitres() {
    // Réinitialiser la pagination à la première page lors de la recherche
    this.pageable.page = 0;
    this.pageable.first = 0;
    this.loadTitres();
  }

  onReset() {
    this.searchForm.reset();
    this.rangeDates = [];
    this.minDate = null;
    this.pageable.page = 0;
    this.pageable.first = 0;
    this.loadTitres();
  }

  onUpload(event: any) {
    const file = event.files[0];
    if (file) {
      this.emissionService.uploadEmissions(file)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.toastService.showToast('Succès', 'Le fichier a été importé avec succès.', 'success');

            // Réinitialiser les filtres après import
            this.searchForm.reset();
            this.rangeDates = [];
            this.minDate = null;

            this.loadTitres();
            this.resetFileUpload();
          },
          error: (error) => {
            this.toastService.showToast('Erreur', 'Erreur lors de l\'import du fichier', 'error');
            console.error(error);
            this.resetFileUpload();
          }
        });
    }
  }

  resetFileUpload() {
    // Reset the file upload component
    const fileUpload = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileUpload) {
      fileUpload.value = '';
    }
  }
}
